import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import Loader from '../../../shared/components/ui/Loader';
import {
  archiveUser,
  getAllUsers,
  getArchivePreflight,
  getUserById
} from '../../services/api/implementation/impl';
import {
  ARCHIVE_INVALIDATION_KEYS,
  BLOCKER_LABELS,
  candidatesForScope,
  canPurgeUsers,
  formatUserName
} from '../../utils/userArchiveAccess';

/**
 * Archiving a user.
 *
 * Three lists, in this order, because that is the order the question gets
 * answered: what must be handed over, what happens by itself, and what is kept.
 * The third one is why the action feels safe to take — it is the answer to
 * "what happens to their work".
 */
const ArchiveUserModal = ({ user, currentUser, onClose, onArchived }) => {
  // One successor per category — a departing manager's leads and their direct
  // reports rarely belong with the same person.
  const [successors, setSuccessors] = useState({});
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState('temporary');
  const queryClient = useQueryClient();

  const userId = user?.id;

  // The list table self-fetches, so it hands us an id and nothing else.
  const { data: target } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId),
    enabled: Boolean(userId)
  });
  const displayName = target?.name || user?.name || 'this user';

  const {
    data: preflight,
    isLoading,
    error
  } = useQuery({
    queryKey: ['archivePreflight', userId],
    queryFn: () => getArchivePreflight(userId),
    enabled: Boolean(userId)
  });

  // Successor picker: live users only. Nobody archived should ever become
  // newly responsible for open work.
  const { data: usersPayload } = useQuery({
    queryKey: ['allUsers', 'successorPicker'],
    queryFn: () => getAllUsers(1, 500),
    enabled: Boolean(userId)
  });

  const allUsers = usersPayload?.data ?? [];

  const blockers = preflight?.blockers ?? [];
  const autoHandled = preflight?.autoHandled ?? [];
  const preserved = preflight?.preserved ?? {};
  const refusal = preflight?.refusal ?? null;
  const successorRequired = blockers.length > 0;

  const { mutate: submit, isLoading: isSubmitting } = useMutation({
    mutationFn: () =>
      archiveUser(userId, {
        successors: successorRequired ? successors : undefined,
        reason: reason.trim(),
        mode
      }),
    onSuccess: (result) => {
      toast.success(
        mode === 'permanent'
          ? `${displayName} removed permanently`
          : `${displayName} moved to archived users`
      );
      if (result?.purgeWarning) {
        // The archive landed but the scrub did not. Saying "done" would be a lie.
        toast.error(`Archived, but not fully removed: ${result.purgeWarning}`);
      }
      // Without this the archived user lingers in every cached dropdown.
      ARCHIVE_INVALIDATION_KEYS.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key })
      );
      Object.values(successors).forEach((id) =>
        queryClient.invalidateQueries({ queryKey: ['user', Number(id)] })
      );
      onArchived?.(result);
      onClose?.();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Could not archive this user'
      );
    }
  });

  const allAssigned = blockers.every((b) => successors[b.type]);
  const canSubmit = !refusal && reason.trim().length > 0 && (!successorRequired || allAssigned);
  const mayPurge = canPurgeUsers(currentUser);

  const preservedEntries = Object.entries(preserved).filter(([, n]) => Number(n) > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <Loader isVisible={isSubmitting} />

        <h2 className="text-lg font-semibold text-gray-900">
          Archive {displayName}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Their login is deleted and their email and phone are released. Their record
          and history stay, so reports and past work keep their name.
        </p>

        {isLoading && (
          <p className="mt-6 text-sm text-gray-500">Checking what they own…</p>
        )}

        {error && (
          <p className="mt-6 text-sm text-red-600">
            {error?.response?.data?.message || 'Could not load this user’s details'}
          </p>
        )}

        {refusal && (
          <div className="mt-5 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{refusal.message}</p>
          </div>
        )}

        {!isLoading && !refusal && (
          <>
            {blockers.length > 0 && (
              <section className="mt-5">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Hand over to
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Each list can go to a different person. Only people on the
                  relevant projects are offered.
                </p>
                <div className="mt-3 space-y-3">
                  {blockers.map((b) => {
                    const options = candidatesForScope(allUsers, b.scope, userId);
                    return (
                      <div key={b.type}>
                        <label className="block text-sm text-gray-800">
                          {b.label || BLOCKER_LABELS[b.type] || b.type}
                          <select
                            className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                            value={successors[b.type] ?? ''}
                            onChange={(e) =>
                              setSuccessors((prev) => ({
                                ...prev,
                                [b.type]: e.target.value ? Number(e.target.value) : undefined
                              }))
                            }>
                            <option value="">Choose a person…</option>
                            {options.map((u) => (
                              <option key={u.id} value={u.id}>
                                {formatUserName(u)}
                              </option>
                            ))}
                          </select>
                        </label>
                        {options.length === 0 && (
                          <p className="mt-1 text-xs text-red-600">
                            Nobody else is on these projects. Add someone to them
                            first, or reassign this work by hand.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {autoHandled.length > 0 && (
              <section className="mt-5">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Handled automatically
                </h3>
                <ul className="mt-2 space-y-1">
                  {autoHandled.map((a) => (
                    <li key={a.type} className="text-sm text-gray-600">
                      • {a.label}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {preservedEntries.length > 0 && (
              <section className="mt-5 rounded-md border border-green-200 bg-green-50 p-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-green-800">
                  Kept, untouched
                </h3>
                <ul className="mt-2 space-y-1">
                  {preservedEntries.map(([key, n]) => (
                    <li key={key} className="text-sm text-green-900">
                      • {n} {key.replace(/_/g, ' ')} — still shown against their name
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {mayPurge && (
              <fieldset className="mt-5">
                <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  How far to go
                </legend>
                <label className="mt-2 flex items-start gap-2">
                  <input
                    type="radio"
                    className="mt-1"
                    checked={mode === 'temporary'}
                    onChange={() => setMode('temporary')}
                  />
                  <span className="text-sm text-gray-800">
                    Move to archived users
                    <span className="block text-xs text-gray-500">
                      Recoverable. Their details are erased automatically after the
                      retention period.
                    </span>
                  </span>
                </label>
                <label className="mt-2 flex items-start gap-2">
                  <input
                    type="radio"
                    className="mt-1"
                    checked={mode === 'permanent'}
                    onChange={() => setMode('permanent')}
                  />
                  <span className="text-sm text-gray-800">
                    Remove permanently, now
                    <span className="block text-xs text-gray-500">
                      Erases their details immediately. Work is still handed over
                      first. This cannot be undone.
                    </span>
                  </span>
                </label>
              </fieldset>
            )}

            <label className="mt-4 block">
              <span className="text-sm font-medium text-gray-800">Reason</span>
              <input
                type="text"
                className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                placeholder="Resigned 30 Aug 2026"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <span className="mt-1 block text-xs text-gray-500">
                Recorded against this action so it can be explained later.
              </span>
            </label>
          </>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" displayType="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit || isSubmitting} onClick={() => submit()}>
            {mode === 'permanent' ? 'Remove permanently' : 'Move to archived'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ArchiveUserModal;
